import React, { useState, useEffect } from 'react';
import { CapacitorSQLite, SQLiteConnection, SQLiteDBConnection } from '@capacitor-community/sqlite';

class OfflineStorageManager {
  constructor() {
    this.sqlite = new SQLiteConnection(CapacitorSQLite);
    this.database = null;
    this.databaseName = 'pm_labor_bills_db';
    this.databaseVersion = 1;
  }

  async initialize() {
    try {
      // Check if database exists
      const isDbExists = await this.sqlite.isDatabase(this.databaseName);
      
      if (!isDbExists.result) {
        // Create new database
        await this.sqlite.createConnection(
          this.databaseName,
          false, // not encrypted for this example
          'no-encryption',
          this.databaseVersion,
          false
        );
      }

      // Open database connection
      this.database = await this.sqlite.open(
        this.databaseName, 
        false, 
        'no-encryption', 
        this.databaseVersion, 
        false
      );
      
      // Create tables if they don't exist
      await this.createTables();
      
      return true;
    } catch (error) {
      console.error('Database initialization failed:', error);
      return false;
    }
  }

  async createTables() {
    if (!this.database) {
      throw new Error('Database not initialized');
    }

    const createTablesSQL = `
      CREATE TABLE IF NOT EXISTS labor_bills_offline (
        id TEXT PRIMARY KEY,
        pm_user TEXT NOT NULL,
        job_name TEXT NOT NULL,
        order_number TEXT,
        install_date TEXT,
        grand_total REAL DEFAULT 0,
        status TEXT DEFAULT 'draft',
        created_at TEXT NOT NULL,
        updated_at TEXT NOT NULL,
        synced_at TEXT,
        needs_sync INTEGER DEFAULT 1
      );

      CREATE TABLE IF NOT EXISTS photos (
        id TEXT PRIMARY KEY,
        labor_bill_id TEXT NOT NULL,
        photo_data TEXT NOT NULL,
        filename TEXT,
        created_at TEXT NOT NULL,
        FOREIGN KEY (labor_bill_id) REFERENCES labor_bills_offline (id)
      );

      CREATE INDEX IF NOT EXISTS idx_labor_bills_pm_user ON labor_bills_offline (pm_user);
      CREATE INDEX IF NOT EXISTS idx_labor_bills_needs_sync ON labor_bills_offline (needs_sync);
      CREATE INDEX IF NOT EXISTS idx_photos_labor_bill ON photos (labor_bill_id);
    `;

    try {
      await this.database.execute(createTablesSQL);
      console.log('✅ Database tables created successfully');
    } catch (error) {
      console.error('❌ Table creation failed:', error);
      throw error;
    }
  }

  async saveLaborBill(laborBill) {
    if (!this.database) {
      throw new Error('Database not initialized');
    }

    const now = new Date().toISOString();
    const id = laborBill.id || this.generateUUID();

    try {
      await this.database.run(
        `INSERT OR REPLACE INTO labor_bills_offline 
         (id, pm_user, job_name, order_number, install_date, grand_total, status, created_at, updated_at, needs_sync) 
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          id, 
          laborBill.pm_user, 
          laborBill.job_name, 
          laborBill.order_number || null,
          laborBill.install_date || null,
          laborBill.grand_total || 0,
          laborBill.status || 'draft',
          laborBill.created_at || now,
          now, 
          1
        ]
      );

      return id;
    } catch (error) {
      console.error('Labor bill save failed:', error);
      throw error;
    }
  }

  async getLaborBills(pmUser) {
    if (!this.database) {
      throw new Error('Database not initialized');
    }

    try {
      const result = await this.database.query(
        'SELECT * FROM labor_bills_offline WHERE pm_user = ? ORDER BY updated_at DESC',
        [pmUser]
      );

      return result.values || [];
    } catch (error) {
      console.error('Labor bills retrieval failed:', error);
      throw error;
    }
  }

  async savePhoto(laborBillId, photoData, filename) {
    if (!this.database) {
      throw new Error('Database not initialized');
    }

    const id = this.generateUUID();
    const now = new Date().toISOString();

    try {
      await this.database.run(
        `INSERT INTO photos (id, labor_bill_id, photo_data, filename, created_at) 
         VALUES (?, ?, ?, ?, ?)`,
        [id, laborBillId, photoData, filename, now]
      );

      return id;
    } catch (error) {
      console.error('Photo save failed:', error);
      throw error;
    }
  }

  async getPhotosForLaborBill(laborBillId) {
    if (!this.database) {
      throw new Error('Database not initialized');
    }

    try {
      const result = await this.database.query(
        'SELECT * FROM photos WHERE labor_bill_id = ? ORDER BY created_at DESC',
        [laborBillId]
      );

      return result.values || [];
    } catch (error) {
      console.error('Photos retrieval failed:', error);
      throw error;
    }
  }

  generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  async close() {
    if (this.database) {
      await this.sqlite.close(this.databaseName, false);
      this.database = null;
    }
  }
}

const OfflineStorageComponent = ({ currentUser }) => {
  const [storageManager, setStorageManager] = useState(null);
  const [laborBills, setLaborBills] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    initializeStorage();
    return () => {
      if (storageManager) {
        storageManager.close();
      }
    };
  }, []);

  useEffect(() => {
    if (isInitialized && currentUser) {
      loadLaborBills();
    }
  }, [isInitialized, currentUser]);

  const initializeStorage = async () => {
    try {
      const manager = new OfflineStorageManager();
      const success = await manager.initialize();
      
      if (success) {
        setStorageManager(manager);
        setIsInitialized(true);
        console.log('✅ Offline storage initialized successfully');
      } else {
        console.error('❌ Failed to initialize offline storage');
      }
    } catch (error) {
      console.error('❌ Storage initialization error:', error);
    }
  };

  const loadLaborBills = async () => {
    if (!storageManager || !currentUser) return;

    setIsLoading(true);
    try {
      const bills = await storageManager.getLaborBills(currentUser.username);
      setLaborBills(bills);
      console.log(`✅ Loaded ${bills.length} labor bills from offline storage`);
    } catch (error) {
      console.error('❌ Failed to load labor bills:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const createTestLaborBill = async () => {
    if (!storageManager || !currentUser) return;

    setIsLoading(true);
    try {
      const testBill = {
        pm_user: currentUser.username,
        job_name: `Test Job ${new Date().getTime()}`,
        order_number: `ORD-${Math.floor(Math.random() * 10000)}`,
        install_date: new Date().toISOString().split('T')[0],
        grand_total: Math.floor(Math.random() * 5000) + 1000,
        status: 'draft'
      };

      const id = await storageManager.saveLaborBill(testBill);
      console.log('✅ Test labor bill created:', id);
      await loadLaborBills(); // Reload the list
    } catch (error) {
      console.error('❌ Failed to create test labor bill:', error);
      alert('Falha ao criar Labor Bill de teste');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isInitialized) {
    return (
      <div className="offline-storage p-4">
        <div className="text-center">
          <div className="text-4xl mb-2">⏳</div>
          <h3 className="text-lg font-semibold mb-2">Inicializando Armazenamento</h3>
          <p className="text-sm text-gray-600">
            Configurando banco de dados local...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="offline-storage p-4">
      <h3 className="text-lg font-semibold mb-4">💾 Armazenamento Offline</h3>
      
      <div className="storage-controls mb-4">
        <button
          onClick={createTestLaborBill}
          disabled={isLoading}
          className="w-full p-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
        >
          {isLoading ? 'Criando...' : '➕ Criar Labor Bill Teste'}
        </button>
      </div>

      <div className="storage-status mb-4 p-3 bg-blue-50 rounded-lg">
        <h4 className="text-md font-medium text-blue-800">📊 Status do Armazenamento</h4>
        <div className="text-sm text-blue-600 space-y-1">
          <p>✅ Banco SQLite: Inicializado</p>
          <p>📋 Labor Bills salvos: {laborBills.length}</p>
          <p>👤 Usuário atual: {currentUser?.username || 'N/A'}</p>
        </div>
      </div>

      <div className="labor-bills-list">
        <h4 className="text-md font-medium mb-2">📋 Labor Bills Offline ({laborBills.length})</h4>
        
        {isLoading ? (
          <div className="text-center py-4">
            <div className="text-2xl mb-2">⏳</div>
            <p className="text-sm text-gray-600">Carregando...</p>
          </div>
        ) : laborBills.length === 0 ? (
          <div className="text-center py-4 bg-gray-50 rounded-lg">
            <div className="text-2xl mb-2">📭</div>
            <p className="text-sm text-gray-600">
              Nenhum Labor Bill salvo offline ainda.
              <br />
              Clique em "Criar Labor Bill Teste" para começar.
            </p>
          </div>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {laborBills.map((bill) => (
              <div key={bill.id} className="p-3 bg-white border rounded-lg shadow-sm">
                <div className="flex justify-between items-start">
                  <div>
                    <h5 className="font-medium text-sm">{bill.job_name}</h5>
                    <p className="text-xs text-gray-600">
                      Ordem: {bill.order_number || 'N/A'}
                    </p>
                    <p className="text-xs text-gray-600">
                      Total: ${bill.grand_total?.toFixed(2) || '0.00'}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      bill.status === 'draft' 
                        ? 'bg-yellow-100 text-yellow-800' 
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {bill.status}
                    </span>
                    {bill.needs_sync === 1 && (
                      <div className="text-xs text-orange-600 mt-1">
                        🔄 Precisa sincronizar
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export { OfflineStorageManager, OfflineStorageComponent };
export default OfflineStorageComponent;